/**
 * Deterministic grid pathfinding for OVERVOLT Maze Mode — see
 * docs/GAME_DESIGN.md §16.3 / §16.4.
 *
 * Two pure functions over a blocked-tile set, with **no RNG anywhere**:
 *  - {@link findPath} — A* over a 4-neighbour grid (orthogonal moves only,
 *    uniform step cost, Manhattan heuristic) with a fully-specified tie-break
 *    chain, so any given blocked set yields exactly one route.
 *  - {@link floodReachable} — a breadth-first flood-fill from a seed tile,
 *    used by the no-wall-off placement rule (§16.4).
 *
 * Tiles are addressed by a packed index `row * cols + col`.
 */
import type { Cell } from './map';

/** Packed tile index for (col, row) on a `cols`-wide grid. */
export function tileIndex(col: number, row: number, cols: number): number {
  return row * cols + col;
}

/**
 * Orthogonal neighbour offsets in the fixed expansion order **North, South,
 * West, East** — the order that, together with the strict-`g` `cameFrom` rule,
 * makes A* relaxation deterministic (§16.3).
 */
const NEIGHBOURS: ReadonlyArray<readonly [number, number]> = [
  [0, -1], // North
  [0, 1], // South
  [-1, 0], // West
  [1, 0], // East
];

/**
 * Deterministic A* from `start` to `goal` over a 4-neighbour grid.
 *
 * - **Movement graph:** orthogonal only (no diagonals); uniform step cost `1`.
 * - **Heuristic:** Manhattan distance to `goal` — admissible and consistent on
 *   this grid, so the route returned is a guaranteed shortest path.
 * - **Tie-break chain (total — yields exactly one path):** the frontier is
 *   popped by ascending `f = g + h`, then ascending `h`, then ascending tile
 *   index. Neighbours are relaxed in the fixed N/S/W/E order and a `cameFrom`
 *   link is overwritten **only on a strictly-lower `g`**, so the
 *   first-discovered predecessor wins every equal-cost tie.
 *
 * `blocked` holds the tile indices that may not be entered; `start` itself is
 * always expandable (it is the source) even if listed in `blocked`.
 *
 * Returns the tile polyline (`start` … `goal` inclusive), or `null` when the
 * goal is unreachable for the given blocked set.
 *
 * Implementation notes (Sprint 38 perf-wins — `docs/PERFORMANCE_AUDIT.md` §6.3):
 *  - The open list is a **binary min-heap** keyed by `(f, h, tile index)`, so
 *    the per-pop frontier scan is `O(log n)` instead of the previous `O(n)`
 *    linear sweep. The comparator chain is bit-for-bit the original one, so
 *    the popped order — and therefore every cameFrom decision and every
 *    returned route — is byte-identical to the pre-heap implementation.
 *  - `open.includes(ni)` is gone. Lazy deletion handles re-relaxation: when a
 *    node's `g` drops we push a fresh heap entry; on pop we skip entries whose
 *    tile is already `closed`. Because the heap is min-first the freshest
 *    (lowest-`f`) entry always wins, exactly matching the old linear scan's
 *    "pick the smallest `f` over the current open set" semantics.
 *  - The Manhattan heuristic is memoised in `hCache` and computed at most once
 *    per touched tile.
 */
export function findPath(
  start: Cell,
  goal: Cell,
  blocked: ReadonlySet<number>,
  cols: number,
  rows: number,
): Cell[] | null {
  const total = cols * rows;
  const gScore = new Array<number>(total).fill(Infinity);
  const cameFrom = new Array<number>(total).fill(-1);
  const closed = new Array<boolean>(total).fill(false);
  // `-1` sentinel = "not yet computed". A Manhattan distance is non-negative,
  // so any real value is distinguishable from the sentinel.
  const hCache = new Array<number>(total).fill(-1);

  const startIdx = start.row * cols + start.col;
  const goalIdx = goal.row * cols + goal.col;
  const goalCol = goal.col;
  const goalRow = goal.row;

  /** Manhattan distance from tile `idx` to the goal, memoised in `hCache`. */
  const heuristic = (idx: number): number => {
    const cached = hCache[idx]!;
    if (cached >= 0) return cached;
    const col = idx % cols;
    const row = (idx - col) / cols;
    const h = Math.abs(col - goalCol) + Math.abs(row - goalRow);
    hCache[idx] = h;
    return h;
  };

  // Parallel-array binary min-heap over (f, h, idx). Parallel arrays avoid the
  // tuple-allocation cost an `[f, h, idx][]` heap would pay on every push.
  const heapF: number[] = [];
  const heapH: number[] = [];
  const heapI: number[] = [];

  /** Strictly-less comparator matching the original `(f, h, idx)` tie-break. */
  const less = (af: number, ah: number, ai: number, bf: number, bh: number, bi: number): boolean =>
    af < bf || (af === bf && (ah < bh || (ah === bh && ai < bi)));

  const heapPush = (f: number, h: number, i: number): void => {
    let k = heapF.length;
    heapF.push(f);
    heapH.push(h);
    heapI.push(i);
    while (k > 0) {
      const parent = (k - 1) >> 1;
      const pf = heapF[parent]!;
      const ph = heapH[parent]!;
      const pi = heapI[parent]!;
      if (!less(f, h, i, pf, ph, pi)) break;
      heapF[k] = pf;
      heapH[k] = ph;
      heapI[k] = pi;
      heapF[parent] = f;
      heapH[parent] = h;
      heapI[parent] = i;
      k = parent;
    }
  };

  const heapPopIdx = (): number => {
    const topIdx = heapI[0]!;
    const last = heapF.length - 1;
    if (last > 0) {
      heapF[0] = heapF[last]!;
      heapH[0] = heapH[last]!;
      heapI[0] = heapI[last]!;
    }
    heapF.pop();
    heapH.pop();
    heapI.pop();
    const n = heapF.length;
    if (n > 1) {
      let k = 0;
      const kf = heapF[0]!;
      const kh = heapH[0]!;
      const ki = heapI[0]!;
      while (true) {
        const l = 2 * k + 1;
        if (l >= n) break;
        const r = l + 1;
        let best = l;
        let bf = heapF[l]!;
        let bh = heapH[l]!;
        let bi = heapI[l]!;
        if (r < n) {
          const rf = heapF[r]!;
          const rh = heapH[r]!;
          const ri = heapI[r]!;
          if (less(rf, rh, ri, bf, bh, bi)) {
            best = r;
            bf = rf;
            bh = rh;
            bi = ri;
          }
        }
        if (!less(bf, bh, bi, kf, kh, ki)) break;
        heapF[k] = bf;
        heapH[k] = bh;
        heapI[k] = bi;
        heapF[best] = kf;
        heapH[best] = kh;
        heapI[best] = ki;
        k = best;
      }
    }
    return topIdx;
  };

  gScore[startIdx] = 0;
  heapPush(0 + heuristic(startIdx), heuristic(startIdx), startIdx);

  while (heapF.length > 0) {
    const current = heapPopIdx();
    // Lazy deletion: an entry that was re-pushed at a lower `f` already
    // consumed this tile's expansion. Skip the stale duplicate.
    if (closed[current]) continue;

    if (current === goalIdx) {
      // Walk the cameFrom chain back to the start, then reverse.
      const route: Cell[] = [];
      let node = current;
      while (node !== -1) {
        const col = node % cols;
        route.push({ col, row: (node - col) / cols });
        if (node === startIdx) break;
        node = cameFrom[node]!;
      }
      route.reverse();
      return route;
    }

    closed[current] = true;

    const cc = current % cols;
    const cr = (current - cc) / cols;
    const gCur = gScore[current]!;
    for (const [dc, dr] of NEIGHBOURS) {
      const nc = cc + dc;
      const nr = cr + dr;
      if (nc < 0 || nr < 0 || nc >= cols || nr >= rows) continue;
      const ni = nr * cols + nc;
      if (closed[ni] || blocked.has(ni)) continue;
      const tentative = gCur + 1;
      // A strictly-lower g overwrites cameFrom; on an equal-cost tie the
      // first-discovered predecessor (fixed N/S/W/E order) is kept.
      if (tentative < gScore[ni]!) {
        cameFrom[ni] = current;
        gScore[ni] = tentative;
        const h = heuristic(ni);
        // Push regardless of prior membership — lazy deletion above is what
        // keeps duplicate entries from being expanded twice.
        heapPush(tentative + h, h, ni);
      }
    }
  }
  return null;
}

/**
 * Breadth-first flood-fill from `from` across un-blocked tiles. Returns the set
 * of reachable tile indices — the basis of the §16.4 no-wall-off check (a
 * single reverse flood-fill from the Core). `from` is always included.
 */
export function floodReachable(
  from: Cell,
  blocked: ReadonlySet<number>,
  cols: number,
  rows: number,
): Set<number> {
  const reached = new Set<number>();
  const startIdx = from.row * cols + from.col;
  reached.add(startIdx);
  const queue: number[] = [startIdx];
  for (let head = 0; head < queue.length; head += 1) {
    const cur = queue[head]!;
    const cc = cur % cols;
    const cr = (cur - cc) / cols;
    for (const [dc, dr] of NEIGHBOURS) {
      const nc = cc + dc;
      const nr = cr + dr;
      if (nc < 0 || nr < 0 || nc >= cols || nr >= rows) continue;
      const ni = nr * cols + nc;
      if (reached.has(ni) || blocked.has(ni)) continue;
      reached.add(ni);
      queue.push(ni);
    }
  }
  return reached;
}
